<template>
    <div>
        <div v-if="enabled">
            <h3>{{ $t('Form Design') }}</h3>
            <cic-select
                :options="
                    Object.keys(forms).map((x) => {
                        return { value: parseInt(x), label: forms[x].name };
                    })
                "
                :value="formId"
                class="selected-form"
                @input="(val) => $emit('input', 'formId', val)"
            />
            <hr />
        </div>
        <div v-for="(fieldData, fieldName) in filters">
            <table class="field-filters">
                <thead>
                    <tr>
                        <th colspan="3">
                            {{ fields[fieldName].label }}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(filter, index) in fieldData">
                        <td>{{ filter.op }}</td>
                        <td>{{ filter.value }}</td>
                        <td v-if="enabled" style="padding: 0">
                            <IconButton icon="remove-icon" @click="removeFilter(fieldName, index)" />
                        </td>
                    </tr>
                    <tr v-if="enabled">
                        <td>
                            <cic-select
                                :value="values[fieldName] ? values[fieldName].op : '='"
                                :options="[
                                    { value: '=', label: '=' },
                                    { value: '!=', label: '!=' },
                                ]"
                                @input="(val) => updateNewFilter(fieldName, 'op', val)"
                            />
                        </td>
                        <td>
                            <cic-select
                                v-if="fields[fieldName].options"
                                :value="values[fieldName] ? values[fieldName].value : null"
                                :options="fields[fieldName].options"
                                @input="(val) => updateNewFilter(fieldName, 'value', val)"
                            />
                            <cic-textbox
                                v-else
                                :value="values[fieldName] ? values[fieldName].value : null"
                                @input="(val) => updateNewFilter(fieldName, 'value', val)"
                            />
                        </td>
                        <td style="padding: 0">
                            <IconButton icon="add-icon" @click="addFilter(fieldName)" />
                        </td>
                    </tr>
                </tbody>
            </table>
            <hr />
        </div>
        <div v-if="formId && enabled">
            <table class="new-filter-field-controls">
                <tbody>
                    <tr>
                        <td>
                            <cic-select
                                :value="newFilter"
                                :options="
                                    filterFields.map((x) => {
                                        return { value: x, label: fields[x].label };
                                    })
                                "
                                class="new-filter-field-select"
                                @input="(val) => (newFilter = val)"
                            />
                        </td>
                        <td style="padding: 0">
                            <IconButton icon="add-icon" @click="addFilterTable" />
                        </td>
                    </tr>
                </tbody>
            </table>
            <hr />
        </div>
        <div v-if="formId">
            <h3>{{ $t('Field Access') }}</h3>
            <table class="field-access">
                <thead>
                    <tr>
                        <th>{{ $t('Name') }}</th>
                        <th>{{ $t('Viewing Access') }}</th>
                        <th>{{ $t('Editing Access') }}</th>
                        <th v-if="enabled"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(access, fieldName) in fieldAccess">
                        <td>{{ fields[fieldName].label }}</td>
                        <td>{{ accessOptions.find((x) => x.value === access.viewDisplay).label }}</td>
                        <td>{{ accessOptions.find((x) => x.value === access.editDisplay).label }}</td>
                        <td v-if="enabled" style="padding: 0">
                            <IconButton icon="remove-icon" @click="removeFieldAccess(fieldName)" />
                        </td>
                    </tr>
                    <tr v-if="enabled">
                        <td>
                            <cic-select
                                :value="newField.name"
                                :options="
                                    Object.keys(fields)
                                        .filter((x) => !Object.keys(fieldAccess).includes(x))
                                        .map((x) => {
                                            return { value: x, label: fields[x].label };
                                        })
                                "
                                @input="(val) => (newField.name = val)"
                            />
                        </td>
                        <td>
                            <cic-select
                                v-if="newField.name"
                                :value="newField.viewAccess"
                                :options="
                                    accessOptions.filter((x) =>
                                        getViewAccessAllowed(formId, newField.name).includes(x.value),
                                    )
                                "
                                @input="(val) => (newField.viewAccess = val)"
                            />
                        </td>
                        <td>
                            <cic-select
                                v-if="newField.name"
                                :value="newField.editAccess"
                                :options="
                                    accessOptions.filter((x) =>
                                        getEditAccessAllowed(formId, newField.name).includes(x.value),
                                    )
                                "
                                @input="(val) => (newField.editAccess = val)"
                            />
                        </td>
                        <td style="padding: 0">
                            <IconButton icon="add-icon" @click="addFieldAccess" />
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
    import IconButton from '@/components/controls/IconButton';
    import CicSelect from '@/components/inputs/CicSelect';
    import CicTextbox from '@/components/inputs/CicTextbox';

    export default {
        name: 'EditCksObjects',
        components: {
            CicSelect,
            CicTextbox,
            IconButton,
        },
        props: {
            enabled: {
                type: Boolean,
                default: false,
            },
            fieldAccess: {
                type: Object,
                required: true,
            },
            fields: {
                type: Object,
                required: true,
            },
            filters: {
                type: Object,
                required: true,
            },
            forms: {
                type: Object,
                required: true,
            },
            formId: {
                type: Number,
                default: null,
            },
        },
        emits: ['input'],
        data() {
            return {
                accessOptions: [
                    { value: 0, label: this.$t('Default') },
                    { value: 1, label: this.$t('Not Visible (takes space in form)') },
                    { value: 2, label: this.$t('Hidden (takes no space in form)') },
                    { value: 3, label: this.$t('Read Only') },
                    { value: 4, label: this.$t('Read Only in Browser') },
                ],
                excludeInput: [
                    'approval',
                    'charges',
                    'comments',
                    'date',
                    'datetime',
                    'duration',
                    'file',
                    'incrementingId',
                    'itemSelect',
                    'itemMultiselect',
                    'reference',
                    'remoteSelect',
                    'organisationSelect',
                    'organisationMultiselect',
                    'userSelect',
                    'userMultiselect',
                ],
                newField: { name: null, viewAccess: 0, editAccess: 0 },
                newFilter: null,
                values: {},
            };
        },
        computed: {
            filterFields() {
                return Object.keys(this.fields).filter(
                    (x) => !Object.keys(this.filters).includes(x) && !this.excludeInput.includes(this.fields[x].input),
                );
            },
        },
        methods: {
            addFieldAccess() {
                if (this.newField.name && this.newField.viewAccess !== null && this.newField.editAccess !== null) {
                    let data = JSON.parse(JSON.stringify(this.fieldAccess));
                    data[this.newField.name] = {
                        viewDisplay: this.newField.viewAccess,
                        editDisplay: this.newField.editAccess,
                    };
                    this.newField = { name: null, viewAccess: 0, editAccess: 0 };
                    this.$emit('input', 'fieldAccess', data);
                }
            },
            addFilter(fieldName) {
                let data = JSON.parse(JSON.stringify(this.filters));
                data[fieldName].push({ value: this.values[fieldName].value, op: this.values[fieldName].op });
                this.$emit('input', 'filters', data);
            },
            addFilterTable() {
                if (this.newFilter) {
                    let data = JSON.parse(JSON.stringify(this.filters));
                    data[this.newFilter] = [];
                    this.values[this.newFilter] = { value: null, op: '=' };
                    this.newFilter = null;
                    this.$emit('input', 'filters', data);
                }
            },
            getEditAccessAllowed(formId, fieldName) {
                if (this.forms[formId.toString()] && this.forms[formId.toString()].fields[fieldName]) {
                    let field = this.forms[formId.toString()].fields[fieldName];
                    if (field.editDisplay === 1 || field.editDisplay === 2) {
                        return [1, 2];
                    } else if (field.editDisplay === 3) {
                        return [3];
                    } else if (field.editDisplay === 4) {
                        return [3, 4];
                    }
                }

                return [0, 1, 2, 3, 4];
            },
            getViewAccessAllowed(formId, fieldName) {
                if (this.forms[formId.toString()] && this.forms[formId.toString()].fields[fieldName]) {
                    let field = this.forms[formId.toString()].fields[fieldName];
                    if (field.viewDisplay === 1 || field.viewDisplay === 2) {
                        return [1, 2];
                    }
                }

                return [0, 1, 2];
            },
            removeFieldAccess(fieldName) {
                let data = JSON.parse(JSON.stringify(this.fieldAccess));
                delete data[fieldName];
                this.$emit('input', 'fieldAccess', data);
            },
            removeFilter(fieldName, index) {
                let data = JSON.parse(JSON.stringify(this.filters));
                data[fieldName].splice(index, 1);
                if (data[fieldName].length === 0) {
                    delete data[fieldName];
                    delete this.values[fieldName];
                }

                this.$emit('input', 'filters', data);
            },
            updateNewFilter(fieldName, field, val) {
                let values = JSON.parse(JSON.stringify(this.values));
                if (!Object.keys(values).includes(fieldName)) {
                    values[fieldName] = {};
                }
                values[fieldName][field] = val;
                this.values = values;
            },
        },
    };
</script>
